import React, { useContext, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import Axios from 'axios';
import { useImmerReducer } from 'use-immer';
import { Alert, Grid, Button, Modal, TextField, Typography, IconButton, Snackbar, Autocomplete } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import StateContext from "../../Contexts/StateContext";
import { formatDate } from '../../Utils/DateUtils';

const EditPublication = ({ open, onClose, publication }) => {
    const { t, i18n } = useTranslation();
    const { config, environment } = require('../../index');
    const navigate = useNavigate();
    const GlobalState = useContext(StateContext);
    const userId = GlobalState.userId;

    const initialState = {
        publicationTitle: '',
        publicationDate: dayjs(),
        publicationPlace: '',
        publicationType: '',
        publicationPublisher: '',
        publicationFullReference: '',
        sendRequest: 0,
        scientist: null,
        scientistFullNames: [],
        openSnack: false,
        disabledEditPublicationBtn: false,
        TitleError: { hasError: false, errorMessage: '' },
        DateError: { hasError: false, errorMessage: '' },
        FullReferenceError: { hasError: false, errorMessage: '' },
        ScientistError: { hasError: false, errorMessage: '' },
    };

    function ReducerFunction(draft, action) {
        switch (action.type) {
            case 'loadPublication':
                draft.publicationTitle = action.payload.title || '';
                draft.publicationDate = action.payload.publication_date ? dayjs(action.payload.publication_date) : dayjs();
                draft.publicationPlace = action.payload.publication_place || '';
                draft.publicationType = action.payload.publication_type || '';
                draft.publicationPublisher = action.payload.publisher || '';
                draft.publicationFullReference = action.payload.full_reference || '';
                draft.scientist = action.payload.scientist_id || null;
                break;
            case 'catchScientistChange':
                draft.scientist = action.scientistChosen;
                draft.ScientistError = { hasError: false, errorMessage: '' };
                break;
            case 'catchPublicationTitleChange':
                draft.publicationTitle = action.publicationTitleChosen;
                draft.TitleError = { hasError: false, errorMessage: '' };
                break;
            case 'catchPublicationDateChange':
                draft.publicationDate = action.publicationDateChosen;
                draft.DateError = { hasError: false, errorMessage: '' };
                break;
            case 'catchPublicationTypeChange':
                draft.publicationType = action.publicationTypeChosen;
                break;
            case 'catchPublicationPlaceChange':
                draft.publicationPlace = action.publicationPlaceChosen;
                break;
            case 'catchPublicationPublisherChange':
                draft.publicationPublisher = action.publicationPublisherChosen;
                break;
            case 'catchPublicationFullReferenceChange':
                draft.publicationFullReference = action.publicationFullReferenceChosen;
                draft.FullReferenceError = { hasError: false, errorMessage: '' };
                break;
            case 'catchScientistFullNames':
                draft.scientistFullNames = action.scientistFullNames || [];
                break;
            case 'changeSendRequest':
                draft.sendRequest += 1;
                draft.disabledEditPublicationBtn = true;
                break;
            case 'openTheSnack':
                draft.openSnack = true;
                break;
            case 'allowEditPublicationBtn':
                draft.disabledEditPublicationBtn = false;
                break;
            case 'emptyTitle':
                draft.TitleError = { hasError: true, errorMessage: t('Validation.EmptyTitle') };
                break;
            case 'emptyDate':
                draft.DateError = { hasError: true, errorMessage: t('Validation.EmptyDate') };
                break;
            case 'emptyFullReference':
                draft.FullReferenceError = { hasError: true, errorMessage: t('Validation.EmptyFullReference') };
                break;
            case 'emptyScientist':
                draft.ScientistError = { hasError: true, errorMessage: t('Validation.EmptyAuthor') };
                break;
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);

    useEffect(() => {
        if (open && publication) {
            dispatch({ type: 'loadPublication', payload: publication });
        }
    }, [open, publication, dispatch]);

    useEffect(() => {
        const endpoint = config[environment].scientistIdWithFullNameEndpoint;
        let isMounted = true;
        const fetchScientists = async () => {
            try {
                const res = await Axios.get(endpoint);
                if (!isMounted) return;
                dispatch({ type: 'catchScientistFullNames', scientistFullNames: res.data });
            } catch (err) {
                console.error(err);
            }
        };
        fetchScientists();
        return () => { isMounted = false; };
    }, [config, environment, dispatch]);

    useEffect(() => {
        if (!state.sendRequest) return;

        const data = new FormData();
        data.append('title', state.publicationTitle);
        data.append('publication_date', formatDate(state.publicationDate));
        data.append('publication_type', state.publicationType);
        data.append('publication_place', state.publicationPlace);
        data.append('publisher', state.publicationPublisher);
        data.append('full_reference', state.publicationFullReference);
        data.append('contributor', userId);
        data.append('scientist', state.scientist);

        const updatePublication = async () => {
            try {
                const endpoint = config[environment].updatePublicationEndpoint(publication.id);
                await Axios.patch(endpoint, data);
                dispatch({ type: 'openTheSnack' });
                window.location.reload();
            } catch (err) {
                dispatch({ type: 'allowEditPublicationBtn' });
                console.error(err);
            }
        };

        updatePublication();
    }, [state.sendRequest, config, environment, userId, publication?.id, state.publicationTitle, state.publicationDate, state.publicationType, state.publicationPlace, state.publicationPublisher, state.publicationFullReference, state.scientist, dispatch]);

    function FormSubmit(e) {
        e.preventDefault();
        if (!state.publicationTitle) dispatch({ type: 'emptyTitle' });
        else if (!state.publicationDate) dispatch({ type: 'emptyDate' });
        else if (!state.publicationFullReference) dispatch({ type: 'emptyFullReference' });
        else if (!state.scientist) dispatch({ type: 'emptyScientist' });
        else dispatch({ type: 'changeSendRequest' });
    }

    const selectedScientist = state.scientistFullNames.find(s => s.id === state.scientist) || null;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Modal open={open} onClose={onClose}>
                <form onSubmit={FormSubmit}>
                    <Grid
                        container direction="column" alignItems="stretch" justifyContent="center"
                        sx={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            width: '90%', maxWidth: 600, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                        }}
                    >
                        <Grid container justifyContent="space-between">
                            <Typography variant='h5'>{t('Academy.EditPublication')}</Typography>
                            <IconButton onClick={onClose}><CloseIcon /></IconButton>
                        </Grid>

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={state.scientistFullNames}
                                    getOptionLabel={(option) => `${option.full_name} (${option.fields_of_science_name})`}
                                    value={selectedScientist}
                                    onChange={(e, value) => dispatch({ type: 'catchScientistChange', scientistChosen: value?.id })}
                                    renderInput={(params) =>
                                        <TextField {...params} label={t('Academy.Author')} required fullWidth variant="standard" error={state.ScientistError.hasError} helperText={state.ScientistError.errorMessage} />}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('Academy.PublicationTitle')} required fullWidth variant="standard"
                                    value={state.publicationTitle}
                                    onChange={(e) => dispatch({ type: 'catchPublicationTitleChange', publicationTitleChosen: e.target.value })}
                                    error={state.TitleError.hasError} helperText={state.TitleError.errorMessage}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label={t('Academy.PublicationDate')} value={state.publicationDate}
                                    onChange={(newValue) => dispatch({ type: 'catchPublicationDateChange', publicationDateChosen: newValue })}
                                    slotProps={{ textField: { fullWidth: true, variant: 'standard', error: state.DateError.hasError, helperText: state.DateError.errorMessage } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t('Academy.PublicationType')} fullWidth variant="standard"
                                    value={state.publicationType}
                                    onChange={(e) => dispatch({ type: 'catchPublicationTypeChange', publicationTypeChosen: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t('Academy.PublicationPlace')} fullWidth variant="standard"
                                    value={state.publicationPlace}
                                    onChange={(e) => dispatch({ type: 'catchPublicationPlaceChange', publicationPlaceChosen: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t('Academy.Publisher')} fullWidth variant="standard"
                                    value={state.publicationPublisher}
                                    onChange={(e) => dispatch({ type: 'catchPublicationPublisherChange', publicationPublisherChosen: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('Academy.FullReference')} required fullWidth variant="standard" multiline rows={3}
                                    value={state.publicationFullReference}
                                    onChange={(e) => dispatch({ type: 'catchPublicationFullReferenceChange', publicationFullReferenceChosen: e.target.value })}
                                    error={state.FullReferenceError.hasError} helperText={state.FullReferenceError.errorMessage}
                                />
                            </Grid>
                        </Grid>

                        <Grid container justifyContent="center" sx={{ mt: 3 }}>
                            <Button variant='contained' type='submit' disabled={state.disabledEditPublicationBtn}>
                                {t('Common.Save', 'MENTÉS')}
                            </Button>
                        </Grid>
                    </Grid>

                    <Snackbar open={state.openSnack} autoHideDuration={6000}>
                        <Alert severity="success" sx={{ width: '100%' }}>{t('Academy.PublicationUpdated', 'Publikáció sikeresen frissítve!')}</Alert>
                    </Snackbar>
                </form>
            </Modal>
        </LocalizationProvider>
    );
};

export default EditPublication;
